import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { DomSanitizer } from '@angular/platform-browser';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { AvmDateAndTimePipe } from '@hub4b/pipes/avm-date-and-time.pipe';
import { DialogAddCommentData } from 'src/app/components/dialogs/add-comment/models/dialog-add-comment-data';
import { DocumentType } from 'src/app/models/enums/document-type';
import { CommentInput } from 'src/app/models/server/comment-input';
import { CommentState } from 'src/app/models/server/enums/comment-state';
import { Rights } from 'src/app/models/server/enums/rights';
import { DialogService } from 'src/app/services/actions/dialog.service';
import { ConnectedUsersStateService } from 'src/app/services/state/connected-users-state.service';

@Component({
    selector: 'app-comment-viewer',
    templateUrl: './comment-viewer.component.html',
    styleUrls: ['./comment-viewer.component.scss'],
    imports: [MatSlideToggleModule, FormsModule, NgClass, MatButtonModule, FaIconComponent, AvmDateAndTimePipe]
})
export class CommentViewerComponent {
    /**
     * Références des icônes utilisés dans le template.
     */
    faPen = faPen;
    faTrash = faTrash;

    /**
     * Références des Enums utilisées dans le template.
     */
    public CommentState = CommentState;
    public DocumentType = DocumentType;

    /**
     * Valeur indiquant si le composant est affiché en mode consultation.
     */
    @Input()
    public readonly: boolean;

    /**
     * Type de commentaire.
     */
    @Input()
    public commentType: DocumentType;

    /**
     * Définit les lignes de commentaires affichées par le composant.
     */
    @Input()
    set comments(value: CommentInput[]) {
        this.sortedComments = this.sort(value);
        // Stoke le texte du commentaire dans une propriété indiquant au navigateur que le texte est SAFE (du fait que l'on affiche le texte dans l'attribut InnerHtml pour qu'il soit interprété au format HTML).
        this.sortedComments.forEach((x) => (x.safeText = this.sanitizer.bypassSecurityTrustHtml(x.text)));
        this.fillOwnership();
    }

    /**
     * Emis lorsque l'utilisateur édite un commentaire.
     */
    @Output()
    public commentEdited: EventEmitter<CommentInput> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur supprime un commentaire.
     */
    @Output()
    public commentDeleted: EventEmitter<CommentInput> = new EventEmitter();

    /**
     * Liste des lignes de commentaire triées.
     */
    public sortedComments: CommentInput[];

    /**
     * Valeur indiquant si l'historique des modifications apportées au commentaire est visible.
     */
    public commentHistoryVisible: boolean = false;

    /**
     * Valeur indiquant si l'utilisateur connecté possède le droit de modifier les lignes de commentaires.
     */
    public UpdateCommentRight: boolean;

    /**
     * Valeur indiquant si l'utilisateur connecté possède le droit de supprimer les lignes de commentaires.
     */
    public DeleteCommentRight: boolean;

    /**
     * Valeur indiquant si l'utilisateur connecté possède le droit de visualiser l'historique des modifications apportées au commentaire.
     */
    public ViewCommentHistoryRight: boolean;

    /**
     * Constructeur.
     * @param dialogService
     * @param connectedUserStateService
     */
    constructor(
        private dialogService: DialogService,
        private connectedUserStateService: ConnectedUsersStateService,
        private sanitizer: DomSanitizer
    ) {
        this.UpdateCommentRight = this.connectedUserStateService.hasRight(Rights.UpdateComment);
        this.DeleteCommentRight = this.connectedUserStateService.hasRight(Rights.DeleteComment);
        this.ViewCommentHistoryRight = this.connectedUserStateService.hasRight(Rights.ViewCommentHistory);
    }

    /**
     * Affiche la boite de dialogue d'édition d'une ligne de commentaire.
     */
    public onEditComment(comment: CommentInput): void {
        if (!this.readonly && this.UpdateCommentRight) {
            this.dialogService.openEditComment(comment).subscribe((updatedCommentData: DialogAddCommentData) => {
                if (!!updatedCommentData) {
                    // Deep clone
                    var updatedComment = JSON.parse(JSON.stringify(comment)) as CommentInput;
                    updatedComment.text = updatedCommentData.text;
                    this.commentEdited.next(updatedComment);
                }
            });
        }
    }

    /**
     * Supprime une ligne de commentaire.
     */
    public onDeleteComment(comment: CommentInput): void {
        if (!this.readonly && this.DeleteCommentRight) {
            this.dialogService.openDeleteComment(comment).subscribe((confirmation: boolean) => {
                if (confirmation === true) {
                    this.commentDeleted.next(comment);
                }
            });
        }
    }

    /**
     * Tri les lignes de commentaires.
     * @param comments Liste des lignes de commentaires.
     * @returns La liste des lignes de commentaires triées par date décroissante.
     */
    private sort(comments: CommentInput[]): CommentInput[] {
        return comments.sort((a, b) => {
            var comparaison = new Date(b.date).getTime() - new Date(a.date).getTime();

            if (comparaison === 0) {
                comparaison = b.iteration - a.iteration;
            }

            return comparaison;
        });
    }

    /**
     * Alimente pour chaque ligne de commentaire une valeur indiquant si l'utilisateur connecté est le propriétaire de la ligne de commentaire.
     */
    private fillOwnership(): void {
        this.sortedComments.forEach((x) => {
            if (x.userId === this.connectedUserStateService.getUserConnected().userId) {
                x.ownedByCurrentUser = true;
            }
        });
    }
}
